import React, { useMemo, useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Wallet, Plus, PencilSimple, Trash, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const COST_TYPES = ["Employee / Payroll", "Software & Subscriptions", "Office & Rent", "Marketing & Leads", "Telecom & Internet", "Insurance", "Professional Services", "Utilities", "Other"];
const thisMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (m, d) => { const [y, mo] = m.split("-").map(Number); const t = new Date(y, mo - 1 + d, 1); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`; };
const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString([], { month: "long", year: "numeric" });
const blank = (month) => ({ month, cost_type: "", product: "", description: "", amount: "" });

export default function OperatingCosts() {
  const [month, setMonth] = useState(thisMonth());
  const { data: costs = [], isLoading } = useList(["operating-costs", month], `/operating-costs?month=${month}`);
  const invalidate = useInvalidate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const total = costs.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const byType = useMemo(() => Object.entries(costs.reduce((m, c) => ({ ...m, [c.cost_type]: (m[c.cost_type] || 0) + Number(c.amount || 0) }), {})).sort((a, b) => b[1] - a[1]), [costs]);

  const save = async () => {
    if (!form.cost_type || !(Number(form.amount) > 0)) { toast.error("Type of cost and amount are required"); return; }
    setSaving(true);
    try {
      const body = { ...form, amount: Number(form.amount) };
      if (form.id) await api.put(`/operating-costs/${form.id}`, body); else await api.post("/operating-costs", body);
      toast.success("Cost saved"); setForm(null); invalidate(["operating-costs"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.product || c.cost_type}"?`)) return;
    await api.delete(`/operating-costs/${c.id}`); invalidate(["operating-costs"]);
  };

  return (
    <div>
      <PageHeader title="Monthly Operating Cost" subtitle="Track what it costs to run Asterism each month: payroll, software and everything else"
        actions={<Button onClick={() => setForm(blank(month))} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="add-cost-btn"><Plus size={16} className="mr-1.5" />Add Cost</Button>} />
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setMonth(shiftMonth(month, -1))} className="w-9 h-9 rounded-lg border border-slate-200 bg-white grid place-items-center text-slate-500 hover:text-slate-900" data-testid="cost-prev-month"><CaretLeft size={16} /></button>
        <p className="font-heading font-bold text-slate-900 w-44 text-center" data-testid="cost-month">{monthLabel(month)}</p>
        <button onClick={() => setMonth(shiftMonth(month, 1))} className="w-9 h-9 rounded-lg border border-slate-200 bg-white grid place-items-center text-slate-500 hover:text-slate-900" data-testid="cost-next-month"><CaretRight size={16} /></button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 items-start">
        <Panel className="p-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Total This Month</p>
          <p className="font-heading font-extrabold text-4xl text-slate-900 mt-1 tabular" data-testid="cost-total">{money(total)}</p>
          <div className="mt-5 space-y-3">
            {byType.length === 0 ? <p className="text-sm text-slate-400">No costs yet.</p> : byType.map(([t, amt]) => (
              <div key={t}>
                <div className="flex justify-between text-sm"><span className="text-slate-600">{t}</span><span className="font-mono font-semibold text-slate-900">{money(amt)}</span></div>
                <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-blue-600" style={{ width: `${total ? (amt / total) * 100 : 0}%` }} /></div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel className="p-0 overflow-hidden">
          {isLoading ? <p className="p-6 text-slate-400">Loading…</p> : costs.length === 0 ? (
            <EmptyState icon={Wallet} title="No Costs Recorded" hint={`Add ${monthLabel(month)} costs like salaries, software and rent.`} />
          ) : (
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50/60"><tr>{["Type of Cost", "Product", "Description", "Amount", ""].map((h) => <th key={h} className={`px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-400 ${h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {costs.map((c) => (
                  <tr key={c.id} data-testid={`cost-row-${c.id}`}>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{c.cost_type}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{c.product || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 max-w-xs">{c.description || "—"}</td>
                    <td className="px-4 py-3 text-sm text-right font-mono font-semibold text-slate-900">{money(c.amount)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setForm({ ...c })} className="p-1.5 text-slate-400 hover:text-blue-600" data-testid={`cost-edit-${c.id}`}><PencilSimple size={15} /></button>
                      <button onClick={() => remove(c)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`cost-delete-${c.id}`}><Trash size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-md" data-testid="cost-dialog">
          <DialogHeader><DialogTitle className="font-heading">{form?.id ? "Edit Cost" : "Add Operating Cost"}</DialogTitle><DialogDescription className="text-slate-500">{form && monthLabel(form.month)}</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-4">
              <Field label="Type of Cost *">
                <Select value={form.cost_type} onValueChange={(v) => setForm({ ...form, cost_type: v })}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="cost-type"><SelectValue placeholder="Select type…" /></SelectTrigger>
                  <SelectContent>{COST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Product"><Input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="e.g. DAT Load Board, John (Dispatcher)" className="bg-white border-slate-300" data-testid="cost-product" /></Field>
              <Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white border-slate-300" data-testid="cost-description" /></Field>
              <Field label="Amount ($) *"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-white border-slate-300 font-mono" data-testid="cost-amount" /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="cost-save">{saving ? "Saving…" : "Save Cost"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
